import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { DateTime } from 'luxon';
import { FC, useEffect, useRef, useState } from 'react';

import { getAssetBookingAvailability } from '@/core/apis/asset-manager-apis';
import { appSettings } from '@/settings/app-settings';

type HorizontalDateSelectorProps = {
  onSelectDate: (date: string) => void;
  nextAvailableDate: string;
  productId: string;
};
const HorizontalDateSelector: FC<HorizontalDateSelectorProps> = ({
  onSelectDate,
  nextAvailableDate,
  productId,
}) => {
  const [startDate, setStartDate] = useState(
    nextAvailableDate ? DateTime.fromJSDate(new Date(nextAvailableDate)) : DateTime.now,
  );
  const [endDate, setEndDate] = useState(startDate?.plus({ days: 30 }));
  const [dateList, setDateList] = useState<DateTime[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(
    nextAvailableDate || DateTime.now().toFormat('yyyy-MM-dd'),
  );
  const containerRef = useRef<any>(null);
  const [scrollLeftValue, setScrollLeftValue] = useState(0);

  useEffect(() => {
    if (productId) {
      fetchBookingAvailability();
    }
  }, [startDate, productId]);

  const fetchBookingAvailability = () => {
    const dateArray: Array<DateTime> = [];
    getAssetBookingAvailability(
      appSettings.api,
      productId,
      startDate.toFormat('yyyy-MM-dd'),
      endDate.toFormat('yyyy-MM-dd'),
    )
      .then((availabilityData) => {
        if (availabilityData) {
          for (let i = 0; i < availabilityData?.dates?.length; i++) {
            dateArray.push(DateTime.fromJSDate(new Date(availabilityData?.dates[i]?.date)));
          }

          setDateList(dateArray);
          onSelectDate(availabilityData?.dates[0]?.date);
          setSelectedDate(availabilityData?.dates[0]?.date);
        }
      })
      .catch((err) => {
        setDateList([]);
      });
  };

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -containerRef.current.clientWidth / 3, behavior: 'smooth' });
      setTimeout(() => {
        setScrollLeftValue(containerRef.current?.scrollLeft);
      }, 300);
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: containerRef.current.clientWidth / 3, behavior: 'smooth' });
      setTimeout(() => {
        setScrollLeftValue(containerRef.current?.scrollLeft);
      }, 300);
    }
  };
  return (
    <div className="w-full overflow-x-auto whitespace-nowrap custom-scrollbar-invisible" ref={containerRef}>
      <button
        className={`absolute top-1/2 left-2 -translate-y-1/2 rounded-full bg-white shadow hidden md:block ${
          scrollLeftValue === 0 ? 'pointer-events-none text-gray-400' : ''
        }`}
      >
        <ChevronLeftIcon className="h-8 w-8" onClick={scrollLeft} />
      </button>
      <div className="inline-flex gap-2">
        {dateList.map((date: DateTime) => {
          return (
            <button
              key={date.toISO()}
              className={`flex-shrink-0 w-[100px] p-2 bg-gray-200 rounded-lg text-center ${
                selectedDate == date.toFormat('yyyy-MM-dd') ? 'border' : 'border-0'
              }`}
              style={{ borderColor: 'black' }}
              onClick={() => {
                setSelectedDate(date.toFormat('yyyy-MM-dd'));
                onSelectDate(date.toFormat('yyyy-MM-dd'));
              }}
            >
              <div className="text-lg font-bold">{date.weekdayShort}</div>
              <div className="text-sm">{date.toFormat('LLL d')}</div>
            </button>
          );
        })}
      </div>
      <button
        className={`absolute top-1/2 right-2 -translate-y-1/2 rounded-full bg-white shadow  hidden md:block ${
          scrollLeftValue + containerRef?.current?.clientWidth >= containerRef?.current?.scrollWidth
            ? 'pointer-events-none text-gray-400'
            : ''
        }`}
      >
        <ChevronRightIcon className="h-8 w-8" onClick={scrollRight} />
      </button>
    </div>
  );
};

export default HorizontalDateSelector;
